import {makeAdUrl} from '@frontend/helpers'
import {AdPreviewPhotosSwiper} from '@frontend/modules/public/ads/components/list/AdPreviewPhotosSwiper'
import {mdiFire} from '@mdi/js'
import {useClampText} from '@shared/helpers/useClampText'
import clsx from 'clsx'
import type {
    CSSProperties,
    MouseEvent,
    RefObject,
} from 'react'
import {Link} from 'react-router-dom'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {useEventCallback} from 'swayok-react-mdb-ui-kit/helpers/useEventCallback'
import {AnalyticsEventReporter} from '../../../../../helpers/AnalyticsEventReporter'
import type {AdPreviewDataForGrid} from '../../AdsModuleTypes'
import {AdsListItemIcons} from './AdsListItemIcons'

interface AdsGridItemProps {
    ad: AdPreviewDataForGrid
    example?: boolean
    className?: string
    gridOrder?: number
    urlTarget?: '_blank' | '_self'
    style?: CSSProperties
}

// Карточка предпросмотра объявления.
export function AdsGridItem(props: AdsGridItemProps) {

    const {
        className,
        ad,
        example,
        urlTarget = '_blank',
        style = {},
        gridOrder = 0,
    } = props

    const [
        adTitleRef,
        {clampedText: adTitle, key: adTitleKey},
    ] = useClampText({
        text: ad.title,
        lines: 2,
        ellipsis: '...',
        expanded: false,
    })

    const [
        locationRef,
        {clampedText: location, key: locationKey},
    ] = useClampText({
        text: ad.location ?? '',
        lines: 2,
        ellipsis: '...',
        expanded: false,
    })

    // Обработчик нажатия на ссылку объявления.
    const onClick = useEventCallback((
        event: MouseEvent<HTMLElement>
    ) => {
        if (example) {
            event.preventDefault()
        }
        AnalyticsEventReporter.clickOnAdFromList(ad)
    })

    return (
        <div
            className={clsx(
                'offered-item-card presentation-grid full-height flex-container-column',
                ad.isPromoted ? 'promoted' : null,
                className
            )}
            style={{
                ...style,
                order: gridOrder,
            }}
        >
            <div className="offered-item-card-image-and-icons position-relative">
                <AdsListItemIcons
                    ad={ad}
                    discounted={!!ad.discountPercent}
                    liked={ad.isFavourite}
                    disabled={example}
                />
                {ad.isPromoted && (
                    <div className="offered-item-card-promoted-label bg-orange rounded-end ps-1 pe-2 fs-7 with-icon-flex fw-500 text-dark">
                        <Icon
                            path={mdiFire}
                            size={20}
                            color="dark"
                        />
                        HOT!
                    </div>
                )}
                <AdPreviewPhotosSwiper ad={ad} />
            </div>
            <div className="offered-item-card-info mt-2 flex-1 flex-container-column">
                <Link
                    className="offered-item-card-title link-blue"
                    target={urlTarget}
                    to={makeAdUrl(ad.id)}
                    rel="noreferrer"
                    ref={adTitleRef as RefObject<HTMLAnchorElement>}
                    key={adTitleKey}
                    onClick={onClick}
                >
                    {ad.photos.length > 0 && (
                        // Для поисковиков.
                        <img
                            src={ad.photos[0]}
                            className="position-absolute opacity-0"
                            width={1}
                            height={1}
                            alt={ad.title}
                        />
                    )}
                    {adTitle}
                </Link>
                <div className="offered-item-card-price mt-2 d-flex flex-row align-items-start">
                    <div className="d-flex flex-column justify-content-start align-items-start align-items-sm-center">
                        <div
                            className={clsx(
                                'offered-item-card-price-value text-nowrap fs-5 fw-600',
                                ad.isPromoted ? 'bg-green text-white px-2 rounded-2' : null
                            )}
                        >
                            {ad.price}
                        </div>
                        {ad.discountPercent && (
                            <div className="text-muted fs-7 text-center text-nowrap">
                                <s>{ad.previousPrice}</s>
                                <span className="d-sm-none ms-1 text-green">
                                    (-{ad.discountPercent}%)
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="offered-item-card-discount d-none d-sm-block">
                        {ad.discountPercent && (
                            <div className="text-green ms-2 ms-sm-3 mt-sm-2 fw-500">
                                -{ad.discountPercent}%
                            </div>
                        )}
                    </div>
                </div>
                {ad.location && (
                    <div
                        className="mt-2 fs-7"
                        ref={locationRef as RefObject<HTMLDivElement>}
                        key={locationKey}
                    >
                        {location}
                    </div>
                )}
                {ad.publishedAt && (
                    <div className="text-muted fs-7 mt-1">
                        {ad.publishedAt}
                    </div>
                )}
            </div>
        </div>
    )
}
